import { useEffect, useState, useCallback } from "react";
import { Bank, Plus, Trash, Power, HandCoins, Scroll, MapPin, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { COUNTRIES, countryName } from "@/lib/countries";
import { money, FINANCE_TYPES, ftLabel } from "@/pages/Finance";

const CURRENCY_OPTIONS = ["usd", "eur", "gbp", "inr", "kes", "ngn", "aed", "cad", "aud", "zar"];
const BLANK_PRODUCT = {
  name: "", finance_type: "working_capital", currency: "usd", min_amount: 5000,
  max_amount: 250000, interest_rate_pct: 12, tenor_min_months: 6, tenor_max_months: 48,
  processing_fee_pct: 1, collateral_required: false, collateral_note: "", countries: [],
  eligibility_note: "", min_turnover: 0, min_years_trading: 0, active: true,
};
const BLANK_BRANCH = { city: "", country: "US", address: "", contact_person: "", phone: "", email: "" };
const BLANK_SCHEME = {
  name: "", country: "US", authority: "", sector: "government",
  finance_types: ["working_capital"], subsidy_pct: 25, ceiling_amount: 100000, currency: "usd",
  min_turnover: 0, max_turnover: 0, min_years_trading: 0, women_owned_only: false,
  msme_only: true, description: "", link: "", active: true,
};

export default function FinanceDesk() {
  const { user } = useAuth();
  const [view, setView] = useState("applications");
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loans, setLoans] = useState([]);
  const [schemeApps, setSchemeApps] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [pForm, setPForm] = useState({ ...BLANK_PRODUCT });
  const [bForm, setBForm] = useState({ ...BLANK_BRANCH });
  const [sForm, setSForm] = useState({ ...BLANK_SCHEME });
  const [offerTo, setOfferTo] = useState(null);
  const [oForm, setOForm] = useState({ amount: 0, interest_rate_pct: 12, tenor_months: 24, processing_fee: 0, conditions: "" });

  const load = useCallback(async () => {
    try {
      const [pf, sm, ap, lo, sa] = await Promise.all([
        api.get("/finance/officer/profile"),
        api.get("/finance/officer/summary"),
        api.get("/finance/officer/applications"),
        api.get("/finance/loans"),
        api.get("/finance/scheme-applications"),
      ]);
      setProfile(pf.data); setSummary(sm.data); setApplications(ap.data);
      setLoans(lo.data); setSchemeApps(sa.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendOffer = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/finance/applications/${offerTo.id}/offer`, {
        amount: Number(oForm.amount),
        interest_rate_pct: Number(oForm.interest_rate_pct),
        tenor_months: Number(oForm.tenor_months),
        processing_fee: Number(oForm.processing_fee),
        conditions: oForm.conditions || null,
      });
      toast.success("Terms offered");
      setOfferTo(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/products", {
        ...pForm,
        lender_id: profile.lender.id,
        min_amount: Number(pForm.min_amount), max_amount: Number(pForm.max_amount),
        interest_rate_pct: Number(pForm.interest_rate_pct),
        tenor_min_months: Number(pForm.tenor_min_months),
        tenor_max_months: Number(pForm.tenor_max_months),
        processing_fee_pct: Number(pForm.processing_fee_pct),
        min_turnover: Number(pForm.min_turnover),
        min_years_trading: Number(pForm.min_years_trading),
      });
      toast.success("Product published");
      setDialog(null);
      setPForm({ ...BLANK_PRODUCT });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveBranch = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/branches", { ...bForm, lender_id: profile.lender.id });
      toast.success("Branch added");
      setDialog(null);
      setBForm({ ...BLANK_BRANCH });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveScheme = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/schemes", {
        ...sForm,
        subsidy_pct: Number(sForm.subsidy_pct), ceiling_amount: Number(sForm.ceiling_amount),
        min_turnover: Number(sForm.min_turnover), max_turnover: Number(sForm.max_turnover),
        min_years_trading: Number(sForm.min_years_trading),
      });
      toast.success("Scheme published");
      setDialog(null);
      setSForm({ ...BLANK_SCHEME });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const decide = async (app, status) => {
    try {
      await api.patch(`/finance/scheme-applications/${app.id}`, { status });
      toast.success(`Claim ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const isSchemeOfficer = profile?.is_scheme_officer;
  const lender = profile?.lender;

  const VIEWS = [
    ["applications", `Applications (${applications.length})`],
    ["loans", `Loan book (${loans.length})`],
    ["products", `Products (${profile?.products?.length || 0})`],
    ["branches", `Branches (${profile?.branches?.length || 0})`],
    ...(isSchemeOfficer ? [["schemes", `My schemes (${profile?.schemes?.length || 0})`],
                          ["claims", `Scheme claims (${schemeApps.length})`]] : []),
  ];

  return (
    <div data-testid="finance-desk-page">
      <PageHeader
        overline={isSchemeOfficer && !lender ? "Government" : "Institution"}
        title="Finance desk"
        description="Applications from businesses in your patch, the terms you offer, your loan book and drawdowns, the products you publish — and, for government officers, the scheme policies you run and the claims against them."
        testid="finance-desk-header"
      />

      {lender && (
        <div className="mb-5 flex flex-wrap items-center gap-2 border border-border bg-white p-3 text-xs text-slate-600" data-testid="lender-identity">
          <span className="text-sm font-semibold text-[#0F172A]">{lender.name}</span>
          <span className="uppercase tracking-wider text-slate-500">{lender.kind?.replace("_", " ")} · {lender.sector}</span>
          {profile.verified
            ? <span className="font-semibold uppercase tracking-wider text-[#002FA7]" data-testid="lender-verified">Verified</span>
            : <StatusBadge value="pending" testid="lender-unverified" />}
          <span data-testid="lender-patch">
            <MapPin size={12} className="mr-1 inline" />
            {lender.city ? `${lender.city}, ` : ""}
            {(lender.countries || []).map(countryName).join(", ") || "Every country"}
            {lender.covers_whole_country === false ? " (city only)" : ""}
            {lender.international ? " + international" : ""}
          </span>
          {!profile.verified && (
            <span className="text-[#B3241B]" data-testid="lender-verify-warning">
              An admin must verify you before you can offer terms
            </span>
          )}
        </div>
      )}
      {!lender && !isSchemeOfficer && (
        <EmptyState testid="no-institution" title="No institution on your account" description="Register your bank or fund under Finance → Register as lender, then an admin verifies you." />
      )}

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-6">
          <StatCard testid="kpi-open-apps" label="To decide" value={summary.open_applications} tone="primary" icon={HandCoins} />
          <StatCard testid="kpi-offers-out" label="Offers out" value={summary.offers_out} />
          <StatCard testid="kpi-loans-written" label="Loans written" value={summary.loans_written} icon={Bank} />
          <StatCard testid="kpi-book-value" label="Book value" value={money(summary.book_value)} />
          <StatCard testid="kpi-drawdowns" label="Awaiting drawdown" value={summary.awaiting_drawdown} tone={summary.awaiting_drawdown > 0 ? "danger" : "default"} />
          <StatCard testid="kpi-scheme-claims" label="Scheme claims" value={summary.scheme_applications} icon={Scroll} />
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="mb-5">
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="desk-views">
          {VIEWS.map(([value, label]) => (
            <TabsTrigger key={value} value={value} data-testid={`desk-view-${value}`} className="whitespace-nowrap">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* ---------------- Applications ---------------- */}
      {view === "applications" && (
        <div className="space-y-3" data-testid="desk-applications-panel">
          {applications.length === 0 ? (
            <EmptyState testid="no-desk-applications" title="No applications yet" description="Applications from businesses in your patch land here." />
          ) : applications.map((a) => (
            <div key={a.id} data-testid={`desk-application-${a.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-bold text-[#002FA7]">{a.reference}</span>
                    <StatusBadge value={a.status} testid={`desk-app-status-${a.id}`} />
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {a.applicant_name} · {ftLabel(a.finance_type)} · {a.product_name} · {countryName(a.country)}
                    {a.city ? `, ${a.city}` : ""}
                  </div>
                  <div className="text-xs text-slate-500">
                    Wants {money(a.amount, a.currency)} over {a.tenor_months} months · turnover {money(a.annual_turnover, a.currency)} ·
                    {" "}{a.years_trading}y trading · {a.employees} staff
                    {a.collateral ? ` · collateral: ${a.collateral} (${money(a.collateral_value, a.currency)})` : " · unsecured"}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{a.purpose}</div>
                  {a.offer && (
                    <div className="mt-2 text-xs text-[#0B7A5B]" data-testid={`desk-offer-${a.id}`}>
                      Offered {money(a.offer.amount, a.currency)} at {a.offer.interest_rate_pct}% · {money(a.offer.emi, a.currency)}/mo
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 sm:shrink-0">
                  {["submitted", "under_review"].includes(a.status) && (
                    <>
                      {a.status === "submitted" && (
                        <Button size="sm" variant="outline" data-testid={`review-${a.id}`} onClick={() => act(() => api.post(`/finance/applications/${a.id}/review`, {}), "Marked under review")}>
                          Review
                        </Button>
                      )}
                      <Button
                        size="sm"
                        data-testid={`offer-terms-${a.id}`}
                        onClick={() => { setOfferTo(a); setOForm({ amount: a.amount, interest_rate_pct: a.indicative_rate_pct, tenor_months: a.tenor_months, processing_fee: 0, conditions: "" }); }}
                      >
                        Offer terms
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`decline-${a.id}`} onClick={() => act(() => api.post(`/finance/applications/${a.id}/decline`, {}), "Application declined")}>
                        Decline
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Loan book ---------------- */}
      {view === "loans" && (
        <div className="space-y-3" data-testid="desk-loans-panel">
          {loans.length === 0 ? (
            <EmptyState testid="no-desk-loans" title="No loans on the book" description="Accepted terms become loans you release and then collect." />
          ) : loans.map((l) => (
            <div key={l.id} data-testid={`desk-loan-${l.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-[#002FA7]">{l.loan_number}</span>
                  <StatusBadge value={l.status === "awaiting_drawdown" ? "pending" : l.status} testid={`desk-loan-status-${l.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {l.borrower_name} · {ftLabel(l.finance_type)} · {l.product_name}
                </div>
                <div className="text-xs text-slate-500">
                  {money(l.principal, l.currency)} at {l.interest_rate_pct}% · {money(l.emi, l.currency)}/mo ·
                  {" "}collected {money(l.paid_total, l.currency)} · outstanding {money(l.outstanding, l.currency)}
                </div>
              </div>
              {l.status === "awaiting_drawdown" && (
                <Button size="sm" data-testid={`drawdown-${l.id}`} onClick={() => act(() => api.post(`/finance/loans/${l.id}/drawdown`), "Funds released")}>
                  Release funds
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Products ---------------- */}
      {view === "products" && (
        <div className="space-y-3" data-testid="desk-products-panel">
          {lender && (
            <div className="flex justify-end">
              <Button data-testid="add-product-button" onClick={() => setDialog("product")}>
                <Plus size={16} className="mr-1.5" /> Publish product
              </Button>
            </div>
          )}
          {(profile?.products || []).length === 0 ? (
            <EmptyState testid="no-desk-products" title="No products published" description="Publish a product so businesses get an instant instalment quote from you." />
          ) : profile.products.map((p) => (
            <div key={p.id} data-testid={`desk-product-${p.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">{p.name}</span>
                  <StatusBadge value={p.active ? "active" : "cancelled"} testid={`desk-product-status-${p.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {ftLabel(p.finance_type)} · {money(p.min_amount, p.currency)}–{money(p.max_amount, p.currency)} ·
                  {" "}{p.interest_rate_pct}% p.a. · {p.tenor_min_months}–{p.tenor_max_months} months · fee {p.processing_fee_pct}%
                  {p.collateral_required ? " · secured" : " · unsecured"}
                </div>
              </div>
              <div className="flex gap-2 sm:shrink-0">
                <Button size="sm" variant="outline" data-testid={`toggle-product-${p.id}`} onClick={() => act(() => api.post(`/finance/products/${p.id}/toggle`), p.active ? "Product withdrawn" : "Product published")}>
                  <Power size={14} className="mr-1" /> {p.active ? "Withdraw" : "Publish"}
                </Button>
                <Button size="sm" variant="ghost" data-testid={`delete-product-${p.id}`} onClick={() => act(() => api.delete(`/finance/products/${p.id}`), "Product removed")}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Branches ---------------- */}
      {view === "branches" && (
        <div className="space-y-3" data-testid="desk-branches-panel">
          {lender && (
            <div className="flex justify-end">
              <Button data-testid="add-branch-button" onClick={() => setDialog("branch")}>
                <Plus size={16} className="mr-1.5" /> Add branch
              </Button>
            </div>
          )}
          {(profile?.branches || []).length === 0 ? (
            <EmptyState testid="no-desk-branches" title="No local branches" description="Add a branch per city so businesses see a named local contact." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {profile.branches.map((b) => (
                <div key={b.id} data-testid={`desk-branch-${b.id}`} className="flex items-start justify-between gap-3 border border-border bg-white p-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A]">{b.city}, {countryName(b.country)}</div>
                    <div className="text-xs text-slate-500">{[b.contact_person, b.phone, b.email].filter(Boolean).join(" · ") || "No contact"}</div>
                    {b.address && <div className="text-xs text-slate-400">{b.address}</div>}
                  </div>
                  <Button size="sm" variant="ghost" data-testid={`delete-branch-${b.id}`} onClick={() => act(() => api.delete(`/finance/branches/${b.id}`), "Branch removed")}>
                    <Trash size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- My schemes ---------------- */}
      {view === "schemes" && (
        <div className="space-y-3" data-testid="desk-schemes-panel">
          <div className="flex justify-end">
            <Button data-testid="add-scheme-button" onClick={() => setDialog("scheme")}>
              <Plus size={16} className="mr-1.5" /> Publish scheme
            </Button>
          </div>
          {(profile?.schemes || []).length === 0 ? (
            <EmptyState testid="no-desk-schemes" title="No schemes published" description="Publish a scheme policy with its subsidy, ceiling and eligibility so businesses can be matched to it." />
          ) : profile.schemes.map((s) => (
            <div key={s.id} data-testid={`desk-scheme-${s.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">{s.name}</span>
                  <StatusBadge value={s.active ? "active" : "cancelled"} testid={`desk-scheme-status-${s.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {s.authority} · {countryName(s.country)} · {s.subsidy_pct}% up to {money(s.ceiling_amount, s.currency)} ·
                  {" "}{(s.finance_types || []).map(ftLabel).join(", ")}
                </div>
              </div>
              <Button size="sm" variant="ghost" data-testid={`delete-scheme-${s.id}`} onClick={() => act(() => api.delete(`/finance/schemes/${s.id}`), "Scheme removed")}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Scheme claims ---------------- */}
      {view === "claims" && (
        <div className="space-y-3" data-testid="desk-claims-panel">
          {schemeApps.length === 0 ? (
            <EmptyState testid="no-desk-claims" title="No scheme claims" description="Claims against your schemes appear here for assessment." />
          ) : schemeApps.map((s) => (
            <div key={s.id} data-testid={`desk-claim-${s.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-[#002FA7]">{s.reference}</span>
                  <StatusBadge value={s.status} testid={`desk-claim-status-${s.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {s.scheme_name} · {s.applicant_name} · asked {money(s.amount, s.currency)} ·
                  {" "}expected support {money(s.expected_support, s.currency)}
                </div>
                <div className="mt-1 text-xs text-slate-500">{s.purpose}</div>
              </div>
              <div className="flex flex-wrap gap-2 sm:shrink-0">
                {["under_review", "approved", "rejected", "disbursed"].map((st) => (
                  <Button
                    key={st}
                    size="sm"
                    variant={st === "disbursed" ? "default" : "outline"}
                    data-testid={`claim-${st}-${s.id}`}
                    onClick={() => decide(s, st)}
                  >
                    {st === "approved" && <CheckCircle size={13} className="mr-1" />}
                    {st.replace("_", " ")}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Offer dialog ---------------- */}
      <Dialog open={!!offerTo} onOpenChange={(v) => !v && setOfferTo(null)}>
        <DialogContent className="sm:max-w-md" data-testid="offer-terms-dialog">
          <DialogHeader>
            <DialogTitle>Offer terms</DialogTitle>
            <DialogDescription>
              {offerTo ? `${offerTo.reference} · ${offerTo.applicant_name} asked ${money(offerTo.amount, offerTo.currency)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendOffer} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Amount</Label>
                <Input data-testid="terms-amount-input" type="number" min="1" required value={oForm.amount} onChange={(e) => setOForm({ ...oForm, amount: e.target.value })} />
              </div>
              <div>
                <Label>Rate % p.a.</Label>
                <Input data-testid="terms-rate-input" type="number" step="0.01" min="0" required value={oForm.interest_rate_pct} onChange={(e) => setOForm({ ...oForm, interest_rate_pct: e.target.value })} />
              </div>
              <div>
                <Label>Tenor (months)</Label>
                <Input data-testid="terms-tenor-input" type="number" min="1" required value={oForm.tenor_months} onChange={(e) => setOForm({ ...oForm, tenor_months: e.target.value })} />
              </div>
              <div>
                <Label>Processing fee</Label>
                <Input data-testid="terms-fee-input" type="number" step="0.01" min="0" value={oForm.processing_fee} onChange={(e) => setOForm({ ...oForm, processing_fee: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Conditions</Label>
              <Textarea data-testid="terms-conditions-input" rows={2} value={oForm.conditions} onChange={(e) => setOForm({ ...oForm, conditions: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-terms-button" className="w-full sm:w-auto">Send terms</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Product dialog ---------------- */}
      <Dialog open={dialog === "product"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>Publish a product</DialogTitle>
            <DialogDescription>Businesses get an instant instalment and cost-of-credit figure from these terms.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveProduct} className="space-y-3">
            <div>
              <Label>Product name</Label>
              <Input data-testid="product-name-input" required value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Finance type</Label>
                <Select value={pForm.finance_type} onValueChange={(v) => setPForm({ ...pForm, finance_type: v })}>
                  <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {FINANCE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={pForm.currency} onValueChange={(v) => setPForm({ ...pForm, currency: v })}>
                  <SelectTrigger data-testid="product-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Minimum amount</Label>
                <Input data-testid="product-min-input" type="number" min="1" value={pForm.min_amount} onChange={(e) => setPForm({ ...pForm, min_amount: e.target.value })} />
              </div>
              <div>
                <Label>Maximum amount</Label>
                <Input data-testid="product-max-input" type="number" min="1" value={pForm.max_amount} onChange={(e) => setPForm({ ...pForm, max_amount: e.target.value })} />
              </div>
              <div>
                <Label>Rate % p.a.</Label>
                <Input data-testid="product-rate-input" type="number" step="0.01" min="0" value={pForm.interest_rate_pct} onChange={(e) => setPForm({ ...pForm, interest_rate_pct: e.target.value })} />
              </div>
              <div>
                <Label>Processing fee %</Label>
                <Input data-testid="product-fee-input" type="number" step="0.01" min="0" value={pForm.processing_fee_pct} onChange={(e) => setPForm({ ...pForm, processing_fee_pct: e.target.value })} />
              </div>
              <div>
                <Label>Min tenor (months)</Label>
                <Input data-testid="product-tenor-min-input" type="number" min="1" value={pForm.tenor_min_months} onChange={(e) => setPForm({ ...pForm, tenor_min_months: e.target.value })} />
              </div>
              <div>
                <Label>Max tenor (months)</Label>
                <Input data-testid="product-tenor-max-input" type="number" min="1" value={pForm.tenor_max_months} onChange={(e) => setPForm({ ...pForm, tenor_max_months: e.target.value })} />
              </div>
              <div>
                <Label>Minimum turnover</Label>
                <Input data-testid="product-turnover-input" type="number" min="0" value={pForm.min_turnover} onChange={(e) => setPForm({ ...pForm, min_turnover: e.target.value })} />
              </div>
              <div>
                <Label>Minimum years trading</Label>
                <Input data-testid="product-years-input" type="number" min="0" value={pForm.min_years_trading} onChange={(e) => setPForm({ ...pForm, min_years_trading: e.target.value })} />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="product-collateral-checkbox" checked={pForm.collateral_required} onChange={(e) => setPForm({ ...pForm, collateral_required: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" />
              Collateral required
            </label>
            <div>
              <Label>Eligibility note</Label>
              <Textarea data-testid="product-eligibility-input" rows={2} value={pForm.eligibility_note} onChange={(e) => setPForm({ ...pForm, eligibility_note: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-product-button" className="w-full sm:w-auto">Publish</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Branch dialog ---------------- */}
      <Dialog open={dialog === "branch"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="branch-dialog">
          <DialogHeader>
            <DialogTitle>Add a local branch</DialogTitle>
            <DialogDescription>A named contact in the city businesses actually walk into.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveBranch} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>City</Label>
                <Input data-testid="branch-city-input" required value={bForm.city} onChange={(e) => setBForm({ ...bForm, city: e.target.value })} />
              </div>
              <div>
                <Label>Country</Label>
                <Select value={bForm.country} onValueChange={(v) => setBForm({ ...bForm, country: v })}>
                  <SelectTrigger data-testid="branch-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contact person</Label>
                <Input data-testid="branch-contact-input" value={bForm.contact_person} onChange={(e) => setBForm({ ...bForm, contact_person: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="branch-phone-input" value={bForm.phone} onChange={(e) => setBForm({ ...bForm, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input data-testid="branch-address-input" value={bForm.address} onChange={(e) => setBForm({ ...bForm, address: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-branch-button" className="w-full sm:w-auto">Add branch</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Scheme dialog ---------------- */}
      <Dialog open={dialog === "scheme"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="scheme-dialog">
          <DialogHeader>
            <DialogTitle>Publish a scheme policy</DialogTitle>
            <DialogDescription>Subsidy, ceiling and eligibility — businesses are matched against these rules.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveScheme} className="space-y-3">
            <div>
              <Label>Scheme name</Label>
              <Input data-testid="scheme-name-input" required value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Authority</Label>
                <Input data-testid="scheme-authority-input" required value={sForm.authority} onChange={(e) => setSForm({ ...sForm, authority: e.target.value })} />
              </div>
              <div>
                <Label>Country</Label>
                <Select value={sForm.country} onValueChange={(v) => setSForm({ ...sForm, country: v })}>
                  <SelectTrigger data-testid="scheme-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subsidy %</Label>
                <Input data-testid="scheme-subsidy-input" type="number" step="0.1" min="0" max="100" value={sForm.subsidy_pct} onChange={(e) => setSForm({ ...sForm, subsidy_pct: e.target.value })} />
              </div>
              <div>
                <Label>Ceiling amount</Label>
                <Input data-testid="scheme-ceiling-input" type="number" min="0" value={sForm.ceiling_amount} onChange={(e) => setSForm({ ...sForm, ceiling_amount: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={sForm.currency} onValueChange={(v) => setSForm({ ...sForm, currency: v })}>
                  <SelectTrigger data-testid="scheme-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Minimum turnover</Label>
                <Input data-testid="scheme-min-turnover-input" type="number" min="0" value={sForm.min_turnover} onChange={(e) => setSForm({ ...sForm, min_turnover: e.target.value })} />
              </div>
              <div>
                <Label>Maximum turnover (0 = none)</Label>
                <Input data-testid="scheme-max-turnover-input" type="number" min="0" value={sForm.max_turnover} onChange={(e) => setSForm({ ...sForm, max_turnover: e.target.value })} />
              </div>
              <div>
                <Label>Minimum years trading</Label>
                <Input data-testid="scheme-years-input" type="number" min="0" value={sForm.min_years_trading} onChange={(e) => setSForm({ ...sForm, min_years_trading: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Funds which finance types</Label>
              <div className="mt-1 flex flex-wrap gap-2" data-testid="scheme-finance-types">
                {FINANCE_TYPES.map(([k, l]) => (
                  <button
                    type="button"
                    key={k}
                    data-testid={`scheme-ft-${k}`}
                    onClick={() => setSForm((f) => ({
                      ...f,
                      finance_types: f.finance_types.includes(k)
                        ? f.finance_types.filter((t) => t !== k)
                        : [...f.finance_types, k],
                    }))}
                    className={`border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                      sForm.finance_types.includes(k)
                        ? "border-[#002FA7] bg-[#002FA7] text-white"
                        : "border-border bg-white text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="scheme-women-checkbox" checked={sForm.women_owned_only} onChange={(e) => setSForm({ ...sForm, women_owned_only: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" />
                Women-owned only
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="scheme-msme-checkbox" checked={sForm.msme_only} onChange={(e) => setSForm({ ...sForm, msme_only: e.target.checked })} className="h-4 w-4 accent-[#002FA7]" />
                MSME only
              </label>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="scheme-description-input" rows={2} value={sForm.description} onChange={(e) => setSForm({ ...sForm, description: e.target.value })} />
            </div>
            <div>
              <Label>Official link</Label>
              <Input data-testid="scheme-link-input" value={sForm.link} onChange={(e) => setSForm({ ...sForm, link: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-scheme-button" className="w-full sm:w-auto">Publish scheme</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
